import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import i18n from 'i18next';

import { Icons, Status, Tooltip } from 'plume-ui';
import { Heading } from 'plume-ui/dist/components/Table/Table';
import { DataRow } from 'plume-ui/src/components/Table/Table';
import React from 'react';

import { STANDARD_DATE_TIME_FORMAT } from '../../../../config';
import { CsvFileSummary } from '../../../../types';
import FormattedMessage from '../../../../utils/components/FormattedMessage';
import { getFrontlineUrl } from '../../../../utils/environments';
import {
  GridItemType,
  Monitor,
  OutreachConfigResponse,
  OutreachConfigResponseWebhook,
  OutreachEnum,
  OutreachSubscriberConfigResponse,
  OutreachTriggerTypes,
  UnresolvedLocationListItem,
  getHarvestSegmentType,
  getOutreachType,
} from '../../types';
import { ClauseType } from '../AddMonitor/types';
import { MonitorCriteriaEnum } from './fixtures';
import { OutreachEvent } from './types';

dayjs.extend(utc);

export const mapMonitorInfoCardData = (
  data: Monitor,
  latestLocations?: number,
  csvTable?: CsvFileSummary[],
): GridItemType[] => {
  if (!data.criteria || latestLocations === undefined) return [];
  const metrics = [
    {
      metric: (
        <React.Fragment>
          <div className="MonitorInfo__metric--with-tooltip">
            <FormattedMessage id="monitors.monitorInfo.monitorInfoMetrics.affectedLocations.title" />
            <Tooltip
              liveMeasure={false}
              label={i18n.t(
                'monitors.monitorInfo.monitorInfoMetrics.affectedLocations.tooltip',
              )}
              position="below"
              alignment="left"
              overrideMaxCharacters
              openInPortal
            >
              <Icons.InfoIcon width={16} />
            </Tooltip>
          </div>
        </React.Fragment>
      ),
      value: latestLocations,
    },
    {
      metric: (
        <FormattedMessage id="monitors.monitorInfo.monitorInfoMetrics.monitorCriteria" />
      ),
      value: (
        <>
          <div className="MonitorInfo__info-card__grid__name">
            {i18n.t('monitorAdd.monitorEvents.title')}
          </div>
          <div>{sequenceDecoder(data.criteria, csvTable)}</div>
          {locationAttributesToString(data.logicalExpression || '') && (
            <>
              <div className="MonitorInfo__info-card__grid__name">
                {i18n.t('monitorAdd.filterLocations.title')}
              </div>
              <div>
                {locationAttributesToString(data.logicalExpression || '')}
              </div>
            </>
          )}
        </>
      ),
    },
    {
      metric: (
        <FormattedMessage id="monitors.monitorInfo.monitorInfoMetrics.subscriberOutreach" />
      ),
      value: data.outreach?.subscriberPush?.status ? (
        <Status
          color={mapOutreachStatusToColor(data.outreach?.subscriberPush.status)}
          label={mapOutreachStatusToLabel(data.outreach?.subscriberPush.status)}
        />
      ) : (
        ''
      ),
    },
    {
      metric: (
        <FormattedMessage id="monitors.monitorInfo.monitorInfoMetrics.supportOutreach" />
      ),
      value: data.outreach?.supportEmail?.status ? (
        <Status
          color={mapOutreachStatusToColor(data.outreach?.supportEmail.status)}
          label={mapOutreachStatusToLabel(data.outreach?.supportEmail.status)}
        />
      ) : (
        ''
      ),
    },
    {
      metric: (
        <FormattedMessage id="monitors.monitorInfo.monitorInfoMetrics.webhookOutreach" />
      ),
      value: data.outreach?.supportWebhook?.status ? (
        <Status
          color={mapOutreachStatusToColor(data.outreach?.supportWebhook.status)}
          label={mapOutreachStatusToLabel(data.outreach?.supportWebhook.status)}
        />
      ) : (
        ''
      ),
    },
    {
      metric: (
        <div className="MonitorInfo__metric--with-tooltip">
          <FormattedMessage id="monitors.monitorInfo.monitorInfoMetrics.harvestSegment.title" />
          <Tooltip
            label={i18n.t(
              'monitors.monitorInfo.monitorInfoMetrics.harvestSegment.tooltip',
            )}
            position="below"
            alignment="left"
            openInPortal
            overrideMaxCharacters
            liveMeasure={false}
          >
            <Icons.InfoIcon width={16} />
          </Tooltip>
        </div>
      ),
      value: data?.harvestSyncEnabled ? (
        <Status
          color={getHarvestSegmentType().enabled.color}
          label={getHarvestSegmentType().enabled.label}
        />
      ) : (
        <Status
          color={getHarvestSegmentType().disabled.color}
          label={getHarvestSegmentType().disabled.label}
        />
      ),
    },
  ];

  if (
    data.issueRelatedWorkflows &&
    Boolean(data.issueRelatedWorkflows.length)
  ) {
    metrics.push({
      metric: (
        <FormattedMessage id="monitors.monitorInfo.monitorInfoMetrics.guidedTroubleshooting" />
      ),
      value: (
        <>
          {data.issueRelatedWorkflows?.map((workflow) => {
            const colors = getHarvestSegmentType();
            return (
              <Status
                key={workflow.workflowName}
                label={workflow.workflowName}
                color={
                  workflow.enabled
                    ? colors.enabled.color
                    : colors.disabled.color
                }
              />
            );
          })}
        </>
      ),
    });
  }

  return metrics;
};

const sequenceDecoder = (
  {
    sequence,
    criterias,
  }: {
    sequence: string;
    criterias: {
      [key: string]: ClauseType;
    };
  },
  csvTable?: CsvFileSummary[],
): string => {
  Object.keys(criterias).forEach((criteria) => {
    const values = criterias[criteria];
    let val: number | string | undefined = values.selectedValue;
    if (
      values.criteriaType === 'excludeList' ||
      values.criteriaType === 'includeList'
    )
      val = csvTable?.find((file) => file.file_id === values.selectedValue)
        ?.name;
    sequence = sequence.replace(
      criteria,
      `${
        MonitorCriteriaEnum[
          values.criteriaType as keyof typeof MonitorCriteriaEnum
        ]
      } ${values.operator} ${val}`,
    );
  });
  return sequence;
};

const locationAttributesToString = (logicalExpression: string) => {
  if (!logicalExpression) return '';
  const split = logicalExpression.split('AND');
  split.splice(0, 1);
  return split.join('AND').trim();
};

export const mapOutreachStatusToColor = (status: keyof typeof OutreachEnum) => {
  return getOutreachType(OutreachEnum[status]).color;
};

export const mapOutreachStatusToLabel = (status: keyof typeof OutreachEnum) => {
  return getOutreachType(OutreachEnum[status]).label;
};

export const mapOutreachInfoCard = (
  data:
    | OutreachConfigResponse
    | OutreachSubscriberConfigResponse
    | OutreachConfigResponseWebhook,
): GridItemType[] => {
  if (data === undefined) return [];
  const isSupportOutreach = (data as OutreachConfigResponse).issueMessageConfig
    ?.recipients;
  const isWebhookOutreach = (data as OutreachConfigResponseWebhook)
    .issueMessageConfig?.webhooks;
  const hasClearMessage = data.notifyIssueClearMessage;
  let result: any[] = [];

  if (isWebhookOutreach) {
    result.push({
      metric: (
        <FormattedMessage id="monitors.monitorInfo.webhookOutreachMetrics.webhookOutreachId" />
      ),
      value: <span>{data.outreachId}</span>,
    });
  }

  result.push({
    metric: (
      <FormattedMessage id="monitors.monitorInfo.supportOutreachMetrics.validTime" />
    ),
    value: (
      <span>
        {`${dayjs(data.startTime)
          .local()
          .format(STANDARD_DATE_TIME_FORMAT)} - `}
        {data.endTime ? (
          dayjs(data.endTime).local().format(STANDARD_DATE_TIME_FORMAT)
        ) : (
          <FormattedMessage id="outreach.form.supportOutreachTimeSection.neverEnds" />
        )}
      </span>
    ),
  });
  if (!isWebhookOutreach) {
    result.push({
      metric: (
        <FormattedMessage id="monitors.monitorInfo.supportOutreachMetrics.trigger" />
      ),
      value: (
        <FormattedMessage
          id={`monitors.monitorInfo.supportOutreachCard.triggerTypes.${data.triggerType}`}
        />
      ),
    });
  } else {
    result.push({
      metric: (
        <FormattedMessage id="monitors.monitorInfo.webhookOutreachMetrics.trigger" />
      ),
      value: (
        <FormattedMessage
          id={`monitors.monitorInfo.supportOutreachCard.triggerTypes.${data.triggerConfig.triggerType}`}
        />
      ),
    });
  }
  if (!isWebhookOutreach && data.triggerType !== OutreachTriggerTypes.Time) {
    result.push({
      metric: (
        <FormattedMessage id="monitors.monitorInfo.supportOutreachMetrics.issueTriggered" />
      ),
      value: (
        <span>
          <FormattedMessage
            id={`monitors.monitorInfo.supportOutreachCard.${data.triggerType}.issueTriggeredWhen`}
          />
          {data.triggerType === OutreachTriggerTypes.CSPOutageDetected &&
            `${data.triggerRelatedInfo.minSubscribersAffected}`}
          {(data.triggerType === OutreachTriggerTypes.MaxPercentage ||
            data.triggerType === OutreachTriggerTypes.MaxIncreaseRate) &&
            `${data.triggerRelatedInfo.issueTriggeringLocationPercentage}`}
          {data.triggerType === OutreachTriggerTypes.MaxValue &&
            `${data.triggerRelatedInfo.issueTriggeringLocationCount}`}
          <FormattedMessage
            id={`monitors.monitorInfo.supportOutreachCard.${data.triggerType}.issueTriggeredWhenSuffix`}
          />
        </span>
      ),
    });
    result.push({
      metric: (
        <FormattedMessage id="monitors.monitorInfo.supportOutreachMetrics.issueCleared" />
      ),
      value: (
        <span>
          <FormattedMessage
            id={`monitors.monitorInfo.supportOutreachCard.${data.triggerType}.issueClearedWhen`}
          />
          {data.triggerType === OutreachTriggerTypes.CSPOutageDetected &&
            `${data.triggerRelatedInfo.issueClearedPercentage}`}
          {(data.triggerType === OutreachTriggerTypes.MaxPercentage ||
            data.triggerType === OutreachTriggerTypes.MaxIncreaseRate) &&
            `${data.triggerRelatedInfo.issueClearingLocationPercentage}`}
          {data.triggerType === OutreachTriggerTypes.MaxValue &&
            `${data.triggerRelatedInfo.issueClearingLocationCount}`}
          <FormattedMessage
            id={`monitors.monitorInfo.supportOutreachCard.${data.triggerType}.issueClearedWhenSuffix`}
          />
        </span>
      ),
    });
  } else if (
    isWebhookOutreach &&
    data.triggerConfig.triggerType !== OutreachTriggerTypes.Time
  ) {
    result.push({
      metric: (
        <FormattedMessage id="monitors.monitorInfo.supportOutreachMetrics.issueTriggered" />
      ),
      value: (
        <span>
          <FormattedMessage
            id={`monitors.monitorInfo.supportOutreachCard.${data.triggerConfig.triggerType}.issueTriggeredWhen`}
          />
          {data.triggerConfig.triggerType ===
            OutreachTriggerTypes.CSPOutageDetected &&
            `${data.triggerConfig.triggerRelatedInfo.minSubscribersAffected}`}
          {(data.triggerConfig.triggerType ===
            OutreachTriggerTypes.MaxPercentage ||
            data.triggerConfig.triggerType ===
              OutreachTriggerTypes.MaxIncreaseRate) &&
            `${data.triggerConfig.triggerRelatedInfo.issueTriggeringLocationPercentage}`}
          {data.triggerConfig.triggerType === OutreachTriggerTypes.MaxValue &&
            `${data.triggerConfig.triggerRelatedInfo.issueTriggeringLocationCount}`}
          <FormattedMessage
            id={`monitors.monitorInfo.supportOutreachCard.${data.triggerConfig.triggerType}.issueTriggeredWhenSuffix`}
          />
        </span>
      ),
    });
    result.push({
      metric: (
        <FormattedMessage id="monitors.monitorInfo.supportOutreachMetrics.issueCleared" />
      ),
      value: (
        <span>
          <FormattedMessage
            id={`monitors.monitorInfo.supportOutreachCard.${data.triggerConfig.triggerType}.issueClearedWhen`}
          />
          {data.triggerConfig.triggerType ===
            OutreachTriggerTypes.CSPOutageDetected &&
            `${data.triggerConfig.triggerRelatedInfo.issueClearedPercentage}`}
          {(data.triggerConfig.triggerType ===
            OutreachTriggerTypes.MaxPercentage ||
            data.triggerConfig.triggerType ===
              OutreachTriggerTypes.MaxIncreaseRate) &&
            `${data.triggerConfig.triggerRelatedInfo.issueClearingLocationPercentage}`}
          {data.triggerConfig.triggerType === OutreachTriggerTypes.MaxValue &&
            `${data.triggerConfig.triggerRelatedInfo.issueClearingLocationCount}`}
          <FormattedMessage
            id={`monitors.monitorInfo.supportOutreachCard.${data.triggerConfig.triggerType}.issueClearedWhenSuffix`}
          />
        </span>
      ),
    });
  }
  if (!isWebhookOutreach) {
    result.push({
      metric: (
        <FormattedMessage id="monitors.monitorInfo.supportOutreachMetrics.maxFrequency" />
      ),
      value: i18n.t(
        `monitors.monitorInfo.xEveryY_${
          (data as OutreachSubscriberConfigResponse).maxNotificationsAllowed
            .recurrenceUnit
        }`,
        {
          number: (data as OutreachConfigResponse).maxNotificationsAllowed
            .maxAllowed,
          count: (data as OutreachSubscriberConfigResponse)
            .maxNotificationsAllowed.recurrence,
        },
      ),
    });
    result.push({
      metric: (
        <FormattedMessage id="monitors.monitorInfo.supportOutreachMetrics.method" />
      ),
      value: isSupportOutreach ? 'Email' : 'Push notification',
    });
    result.push({
      metric: (
        <FormattedMessage id="monitors.monitorInfo.supportOutreachMetrics.message" />
      ),
      value: isSupportOutreach
        ? (data as OutreachConfigResponse).issueMessageConfig?.introduction ===
          undefined
          ? `[${(data as OutreachConfigResponse).issueMessageConfig?.subject}]`
          : `[${
              (data as OutreachConfigResponse).issueMessageConfig?.subject
            }]: ${
              (data as OutreachConfigResponse).issueMessageConfig?.introduction
            }`
        : (data as OutreachSubscriberConfigResponse).issueMessageConfig
            ?.message === undefined
        ? `[${
            (data as OutreachSubscriberConfigResponse).issueMessageConfig
              ?.subject
          }]`
        : `[${
            (data as OutreachSubscriberConfigResponse).issueMessageConfig
              ?.subject
          }]: ${
            (data as OutreachSubscriberConfigResponse).issueMessageConfig
              ?.message
          }`,
    });
    result.push({
      metric: (
        <FormattedMessage id="monitors.monitorInfo.supportOutreachMetrics.clearMessage" />
      ),
      value: hasClearMessage ? (
        <FormattedMessage id="genericLabels.yes" />
      ) : (
        <FormattedMessage id="genericLabels.no" />
      ),
    });
    if (hasClearMessage)
      result.push({
        metric: (
          <FormattedMessage id="monitors.monitorInfo.supportOutreachMetrics.clearMessage" />
        ),
        value: isSupportOutreach
          ? (data as OutreachConfigResponse).clearMessageConfig
              ?.introduction === undefined
            ? `[${
                (data as OutreachConfigResponse).clearMessageConfig?.subject
              }]`
            : `[${
                (data as OutreachConfigResponse).clearMessageConfig?.subject
              }]: ${
                (data as OutreachConfigResponse).clearMessageConfig
                  ?.introduction
              }`
          : (data as OutreachSubscriberConfigResponse).issueMessageConfig
              ?.message === undefined
          ? `[${
              (data as OutreachSubscriberConfigResponse).clearMessageConfig
                ?.subject
            }]`
          : `[${
              (data as OutreachSubscriberConfigResponse).clearMessageConfig
                ?.subject
            }]: ${
              (data as OutreachSubscriberConfigResponse).clearMessageConfig
                ?.message
            }`,
      });
  }
  return result;
};

export const parseOutreachEventsTableData = (data: OutreachEvent[]) =>
  data.map(
    (event: any): DataRow => {
      const hasEndDate = Boolean(event.cleared_at?.length);
      return {
        outreachEvent: event.event_id,
        startTime: dayjs
          .utc(event.created_at)
          .local()
          .format(STANDARD_DATE_TIME_FORMAT),
        endTime: hasEndDate
          ? dayjs
              .utc(event.cleared_at)
              .local()
              .format(STANDARD_DATE_TIME_FORMAT)
          : '',
        eventStatus: hasEndDate
          ? i18n.t(`monitors.outreachEventStatuses.cleared`)
          : i18n.t(`monitors.outreachEventStatuses.notCleared`),
        affectedLocations: event.total_count,
        unresolvedLocations: event.active_count,
        targetAudience: event.target,
        emailSent:
          event.sent_at !== null
            ? dayjs.utc(event.sent_at).local().format(STANDARD_DATE_TIME_FORMAT)
            : '',
        pushNotificationSentCount: event.push_sent_count || 0,
        pushNotificationReceivedCount: event.push_received_count || 0,
        pushNotificationClickedCount: event.push_clicked_count || 0,
        pushNotificationBlockErrorCount:
          event.subscriber_block_count + event.subscriber_error_count || 0,
        webhookCount: event.webhook_count || 0,
        webhookErrorCount: event.webhook_error_count || 0,
      };
    },
  );

export const getUnresolvedLocationsInAlarmHeaders = (): Heading[] => [
  {
    name: i18n.t('monitors.monitorInfo.unresolvedLocations.locationId'),
    fieldName: 'locationId',
    copyable: true,
    sortable: false,
  },
  {
    name: i18n.t('monitors.monitorInfo.unresolvedLocations.accountId'),
    fieldName: 'accountId',
    copyable: true,
    sortable: false,
  },
  {
    name: i18n.t('monitors.monitorInfo.unresolvedLocations.healthScore'),
    fieldName: 'healthScore',
    sortable: false,
  },
  {
    name: i18n.t('monitors.monitorInfo.unresolvedLocations.action'),
    fieldName: 'action',
    sortable: false,
    render: (rowData: DataRow) => (
      <a
        href={rowData.action}
        target="_blank"
        rel="noreferrer"
        className="MonitorInfo__table__link"
      >
        <FormattedMessage id="monitors.monitorInfo.unresolvedLocations.frontline" />
      </a>
    ),
  },
];

export const unresolvedLocationToDataRow = (
  data: UnresolvedLocationListItem,
): DataRow => {
  return {
    key: data.location_id,
    locationId: data.location_id,
    accountId: data.account_id,
    healthScore: String(data.health_score),
    action: `${getFrontlineUrl()}/location/${data.location_id}`,
  };
};

export const rawUnresolvedLocationsToCSV = (
  data: UnresolvedLocationListItem[],
  includeHealthScore = true,
): string[][] => {
  if (!data) return [];
  const csvData = [
    [
      i18n.t('monitors.monitorInfo.unresolvedLocations.customerId'),
      i18n.t('monitors.monitorInfo.unresolvedLocations.locationId'),
      i18n.t('monitors.monitorInfo.unresolvedLocations.accountId'),
      ...(includeHealthScore
        ? [i18n.t('monitors.monitorInfo.unresolvedLocations.healthScore')]
        : []),
    ],
  ];
  return csvData.concat(
    data.map((entry) => {
      return [
        entry.customer_id,
        entry.location_id,
        entry.account_id,
        ...(includeHealthScore ? [String(entry.health_score)] : []),
      ];
    }),
  );
};

export type TimeRange = {
  label: string;
  selected: boolean;
  value: number;
};
export const getTimeRanges = (defaultMonitorRange?: number): TimeRange[] => [
  ...(defaultMonitorRange === 0 || defaultMonitorRange === 1
    ? [
        {
          label: i18n.t('monitors.monitorInfo.monitorTrend.last24hrs'),
          value: 1,
          selected: true,
        },
      ]
    : []),
  ...(defaultMonitorRange === 3
    ? [
        {
          label: i18n.t('monitors.monitorInfo.monitorTrend.last72hrs'),
          value: 3,
          selected: true,
        },
      ]
    : []),
  ...(defaultMonitorRange !== undefined && defaultMonitorRange <= 7
    ? [
        {
          label: i18n.t('monitors.monitorInfo.monitorTrend.last7days'),
          value: 7,
          selected: defaultMonitorRange > 3 ? true : false,
        },
      ]
    : []),
  {
    label: i18n.t('monitors.monitorInfo.monitorTrend.last30days'),
    selected: false,
    value: 30,
  },
  {
    label: i18n.t('monitors.monitorInfo.monitorTrend.last90days'),
    selected: false,
    value: 90,
  },
];

export const parseCriteriaSequence = (criteriaSequence: string) => {
  const lookahead = (string: string, index: number) => {
    for (let i = index; i < string.length; i++) {
      if (string[i] === ' ' && string[i + 1] === ')') return true;
      if (/AND|OR|,../.test(string.slice(i, i + 3))) return false;
    }
    return false;
  };

  if (typeof criteriaSequence !== 'string') return criteriaSequence;

  let charArray: string[] = [];
  let depth = 0;
  let lookaheadFlag = false;

  criteriaSequence.split('').forEach((char, i) => {
    switch (char) {
      case '(': {
        if (criteriaSequence[i + 1] !== ' ') {
          charArray.push(char);
          return;
        }
        if (lookahead(criteriaSequence, i)) {
          charArray.push(char);
          lookaheadFlag = true;
          break;
        }
        charArray.push(char);
        charArray.push('\n');
        depth += 1;
        for (let i = 0; i < depth; i++) {
          charArray.push('\t');
        }
        break;
      }
      case ')': {
        if (criteriaSequence[i - 1] !== ' ') {
          charArray.push(char);
          return;
        }
        if (lookaheadFlag) {
          charArray.push(char);
          lookaheadFlag = false;
          break;
        }
        charArray.push('\n');
        depth -= 1;
        if (depth <= 0) {
          charArray.push(char);
          return;
        }
        for (let i = 0; i < depth; i++) {
          charArray.push('\t');
        }
        charArray.push(char);
        break;
      }
      case ',': {
        charArray.push(char);
        charArray.push('\n');
        for (let i = 0; i < depth; i++) {
          charArray.push('\t');
        }
        break;
      }
      default: {
        charArray.push(char);
      }
    }
  });
  return charArray.join('');
};
